/**
 * @file KnowledgeCitation 工具函数 + 展示类型
 */

import type {
  IDatabaseQueryChunk,
  IDatabaseQueryDocument,
  IDatabaseQueryGroup,
} from '../../types/history';
import { buildGameWikiKbDetailUrl } from '../../utils/kbDetailUrl';

/**
 * 统一展示用的片段数据
 * @interface INormalizedChunk
 */
export interface INormalizedChunk {
  /** 片段文本内容 */
  content: string;
}

/**
 * 统一展示用的文档数据
 * @interface INormalizedDoc
 */
export interface INormalizedDoc {
  /** 文档名称 */
  docName: string;
  /** 文档类型 */
  docType?: string;
  /** 渠道（历史/扩展字段，用于与知识库列表同一套 icon） */
  channel?: string;
  /** 文档跳转链接 */
  docUrl: string;
  /** 文档图标 */
  docIcon?: string;
  /** 片段数量 */
  chunkCount: number;
  /** 片段列表 */
  chunks: INormalizedChunk[];
}

/**
 * 统一展示用的分组数据（一个知识库为一个分组）
 * @interface INormalizedGroup
 */
export interface INormalizedGroup {
  /** 知识库名称 */
  dbName: string;
  /** 知识库类型 */
  dbType?: string;
  /** 文档列表 */
  documents: INormalizedDoc[];
}

/**
 * 打开文档链接（新窗口）
 * @param url - 文档 URL，自动补全前导斜杠
 */
export const openDocUrl = (url?: string): void => {
  if (!url) return;
  let target = url;
  if (target.startsWith('v2')) {
    target = `/${target}`;
  } else if (!target.startsWith('http') && !target.startsWith('/')) {
    target = `/${target}`;
  }
  window.open(target, '_blank');
};

/**
 * 归一化片段列表，过滤空内容
 * @param chunks - 原始片段列表
 * @returns 过滤后的展示用片段列表
 */
export const normalizeChunks = (
  chunks?: IDatabaseQueryChunk[],
): INormalizedChunk[] =>
  (chunks || [])
    .map(chunk => ({ content: (chunk?.content || '').trim() }))
    .filter(chunk => Boolean(chunk.content));

/**
 * 将新协议 database_query 数据归一化为展示结构
 * @param value - database_query 原始数据
 * @returns 按知识库分组的展示数据
 */
export const normalizeDatabaseQuery = (
  value: IDatabaseQueryGroup[],
): INormalizedGroup[] =>
  (value || [])
    .map(group => {
      const documents = (group.documents || [])
        .map((doc: IDatabaseQueryDocument) => {
          const chunks = normalizeChunks(doc.chunks);
          return {
            docName: doc.doc_name || '-',
            docType: doc.doc_type,
            channel: doc.channel,
            docUrl: buildGameWikiKbDetailUrl({
              tenantId: String(doc.tenant_id),
              knowledgeBaseId: doc.knowledge_base_id,
              knowledgeId: doc.knowledge_id,
            }),
            chunkCount: Array.isArray(doc.chunks) ? doc.chunks.length : 0,
            chunks,
          };
        });
      return {
        dbName: group.db_name || '',
        dbType: group.db_type,
        documents,
      };
    });;

